import React, { Component } from 'react';
import { every, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { createSession } from 'actions/inPersonTraining/eventOverview/sessions';
import { Button, Card } from 'ui';
import { VIDEO_CONFERENCE_TYPES } from 'constants/config';
import { isValidURL } from 'utils/string_utils';
import SessionForm from 'components/InPersonTraining/shared/SessionForm';
import TimeZone from 'components/InPersonTraining/shared/SessionForm/TimeZone';
import Error, {
  getErrorMessage,
} from 'components/InPersonTraining/shared/Error';

class AddSessionForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      session: {
        name: 'Session 1',
        trainerId: this.props.currentUser.id,
        startTime: moment().add(1, 'hour').startOf('hour'),
        endTime: moment().add(2, 'hour').startOf('hour'),
        timeZone: TimeZone.getLastSelected(),
        maxAttendees: '',
        location: '',
        videoConferencing: { type: 'none', url: '' },
        capOption: false,
      },
    };
  }

  get isValid() {
    return every([
      moment(this.state.session.startTime).isValid(),
      moment(this.state.session.endTime).isValid(),
      !isEmpty(this.state.session.timeZone),
      this.isVideoMeetingValid(),
    ]);
  }

  isVideoMeetingValid() {
    const { type, url } = this.state.session.videoConferencing;

    if (type !== 'other') return true;

    return isValidURL(url);
  }

  isZoomTimeValid() {
    const startTime = moment(this.state.session.startTime);
    const isStartBeforeCurrent = startTime.isBefore(moment());
    const { type } = this.state.session.videoMeeting;
    let isValid = true;
    if (type === 'zoom' && isStartBeforeCurrent) {
      isValid = false;
    }

    return isValid;
  }

  handleEndTimeChange = endTime => {
    this.setState(prevState => ({
      session: { ...prevState.session, endTime },
    }));
  };

  handleStartTimeChange = (date, e) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState(prevState => ({
      session: {
        ...prevState.session,
        startTime: date,
        endTime: moment(date).set({
          hour: moment(prevState.session.endTime).get('hour'),
          minute: moment(prevState.session.endTime).get('minute'),
        }),
      },
    }));
  };

  handleTimeZoneChange = timeZone => {
    this.setState(
      prevState => ({
        session: { ...prevState.session, timeZone },
      }),
      () => {
        TimeZone.setLastSelected(timeZone);
      },
    );
  };

  handleTrainerChange = trainerId => {
    this.setState(prevState => ({
      session: { ...prevState.session, trainerId },
    }));
  };

  resetMaxAttendees(capOption) {
    if (capOption) return;
    if (!isEmpty(this.state.session.maxAttendees))
      this.setState(prevState => ({
        session: { ...prevState.session, maxAttendees: '' },
      }));
  }

  handleSessionNameChange = (e, sessionId) => {
    const name = e.target.value;
    this.setState(prevState => ({
      session: { ...prevState.session, name },
    }));
  };

  handleCapOptionsChange = e => {
    const capOption = JSON.parse(e.target.value);
    this.setState(
      prevState => ({
        session: { ...prevState.session, capOption },
      }),
      () => {
        this.resetMaxAttendees(capOption);
      },
    );
  };

  handleVideoConferenceChange = data => {
    this.setState(prevState => ({
      session: { ...prevState.session, videoConferencing: data },
    }));
  };

  handleSessionInputChange = e => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      session: { ...prevState.session, [name]: value },
    }));
  };

  handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ loading: true, error: null });

    this.props
      .createSession(this.state.session)
      .then(() => {
        this.setState({ loading: false }, this.props.onDismiss);
      })
      .catch(error => {
        this.setState({
          loading: false,
          error: error?.status === 428 ? null : error,
        });
      });
  };

  render() {
    const { users, timeZones, currentUser, currentCompany } = this.props;
    const { error, session } = this.state;
    const isNewZoomMeeting =
      session.videoConferencing.type === VIDEO_CONFERENCE_TYPES.ZOOM &&
      session.videoConferencing.url === null;
    const errorMessage = error && getErrorMessage(error);
    const isVideoMeetingHost =
      session?.videoConferencing?.host_id === currentUser.id ||
      isNewZoomMeeting ||
      session?.videoConferencing.type !== VIDEO_CONFERENCE_TYPES.ZOOM;

    return (
      <Card.Content className="pt-16">
        {errorMessage && (
          <div className="ErrorContainer ErrorContainer--brandRed mb-16 Animated Animated--fadeIn">
            {errorMessage}
          </div>
        )}
        <form onSubmit={this.handleSubmit}>
          <SessionForm
            viaAddSessionModal
            session={this.state.session}
            users={users}
            currentUser={currentUser}
            currentCompany={currentCompany}
            timeZones={timeZones}
            canEditTime
            isMultiSession
            isVideoMeetingHost={isVideoMeetingHost}
            handleSessionNameChange={this.handleSessionNameChange}
            handleStartTimeChange={this.handleStartTimeChange}
            handleSessionInputChange={this.handleSessionInputChange}
            handleEndTimeChange={this.handleEndTimeChange}
            handleTimeZoneChange={this.handleTimeZoneChange}
            handleTrainerChange={this.handleTrainerChange}
            handleCapOptionsChange={this.handleCapOptionsChange}
            handleVideoConferenceChange={this.handleVideoConferenceChange}
          />
          {error !== null && !error?.message && <Error {...error} />}
          <div className="d-f jc-e ai-c mt-24">
            <Button
              customColor
              className="Button--primary"
              tall
              submit
              loading={this.state.loading}
              disabled={!this.isValid}
            >
              {isNewZoomMeeting ? 'Add Zoom Session' : 'Add Session'}
            </Button>
          </div>
        </form>
      </Card.Content>
    );
  }
}

const mapStateToProps = state => ({
  timeZones: state.meta.timeZones,
  users: state.meta.users,
});

const mapDispatchToProps = {
  createSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddSessionForm);
